























































































































































































































































































































import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import {
  IPayrollChangeRequest,
  User,
  IUser,
  PayrollChangeRequestStatusEnum,
  PayrollChangeRequestStatus
} from '@/models';

import { colors, classToHex } from '@/utils/colorUtils';
import { ErrorManager } from '@/models/error';
import { ExtractVue } from '@/utils/mixins';

import auth from '@/mixins/auth';

import PayrollChangeRequestDetailFormDialog from './PayrollChangeRequestDetailFormDialog.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import PayrollChangeRequestHRPersonnelPicker from './PayrollChangeRequestHRPersonnelPicker.vue';

export default auth.extend({
  name: 'PayrollChangeRequestStateCard',

  components: {
    PayrollChangeRequestDetailFormDialog,
    ConfirmationDialog,
    ErrorAlert,
    PayrollChangeRequestHRPersonnelPicker
  },

  props: {
    payrollChangeRequest: {
      type: Object as PropType<IPayrollChangeRequest>,
      required: true
    }
  },

  data: () => ({
    loading: false,
    hrPersonnelLoading: false,

    errorMessage: '',
    errorMessageDetail: '',
    PayrollChangeRequestStatusEnum,

    submitPayrollChangeRequestApprover: null as null | IUser,
    submitPayrollChangeRequestDialog: false,

    cancelPayrollChangeRequestDialog: false,

    actionLabel: ''
  }),

  computed: {
    ...mapGetters({
      hrPersonnels: 'payrollChangeRequest/getPayrollChangeRequestHRPersonnels'
    }),

    authProvider(): any {
      return this.$altoleapAccounts.authProvider;
    },

    currentUser(): User {
      return this.authProvider.getCurrentUser;
    },

    canApprove(): boolean {
      return (
        this.currentUser.id ===
        (this.payrollChangeRequest.hr_personnel as IUser).id
      );
    },

    isPayrollChangeRequestOwner(): boolean {
      return (
        this.currentUser.id === (this.payrollChangeRequest.owner as IUser).id
      );
    },

    getStatusIcon(): string {
      return this.payrollChangeRequest.state!.getCurrentState().icon!;
    },

    getStatusMessage(): string {
      return this.payrollChangeRequest.state!.getCurrentState().message!;
    },

    getStatusColor(): string {
      return this.payrollChangeRequest.state!.getCurrentState().color!;
    },

    getStatusHexColor(): string {
      return classToHex(
        this.payrollChangeRequest.state!.getCurrentState().color!,
        colors,
        this.$vuetify.theme.currentTheme
      );
    },

    errorOccurred(): boolean {
      return this.errorMessage.length > 0;
    }
  },

  mounted() {
    this.loadNextPayrollChangeRequestHRPersonnels();
  },

  methods: {
    ...mapActions({
      updatePayrollChangeRequestState:
        'payrollChangeRequest/updatePayrollChangeRequestState',
      fetchPayrollChangeRequestHRPersonnels:
        'payrollChangeRequest/fetchPayrollChangeRequestHRPersonnels',
      fetchPayrollChangeRequestActivities:
        'payrollChangeRequest/fetchPayrollChangeRequestActivities'
    }),

    updatePayrollChangeRequest(): void {
      const payrollChangeRequestFormDialog = this.$refs
        .payrollChangeRequestDetailFormDialog as ExtractVue<
        typeof PayrollChangeRequestDetailFormDialog
      >;
      payrollChangeRequestFormDialog.open(this.payrollChangeRequest, true);
    },

    /**
     * @see api payrollChangeRequest/models/start_review
     * can be called only from states:
     * - Draft
     *
     * can go to states
     * - Submitted
     *
     * if completer not assigned then call @see undoSubmit
     */
    submit(assignee: number): void {
      this.clearAllErrors();
      this.hrPersonnelLoading = true;
      this.updatePayrollChangeRequestState({
        payrollChangeRequestId: this.payrollChangeRequest.id,
        state: PayrollChangeRequestStatusEnum.Submitted,
        assignee: assignee
      })
        .then(() => {
          this.submitPayrollChangeRequestDialog = false;
          this.submitPayrollChangeRequestApprover = null;
          this.$emit('submitted');
          this.fetchPayrollChangeRequestActivities(
            this.payrollChangeRequest.id
          );
        })
        .catch(error => {
          if (error.response && error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.hrPersonnelLoading = false;
        });
    },

    /**
     * @see api payrollChangeRequest/models/start_review
     * can be called only from states:
     * - Submitted
     *
     * can go to states
     * - Draft
     */
    undoSubmit() {
      this.clearAllErrors();
      this.loading = true;
      this.updatePayrollChangeRequestState({
        payrollChangeRequestId: this.payrollChangeRequest.id,
        state: PayrollChangeRequestStatusEnum.Draft
      })
        .then(() => {
          this.$emit('undo-submit');
          this.loadNextPayrollChangeRequestHRPersonnels();
        })
        .catch(error => {
          if (error.response && error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * @see api payrollChangeRequest/models/start_review
     * can be called only from states:
     * - Submitted
     *
     * can go to states
     * - InReview
     */
    startReview(): void {
      this.clearAllErrors();
      this.loading = true;
      this.updatePayrollChangeRequestState({
        payrollChangeRequestId: this.payrollChangeRequest.id,
        state: PayrollChangeRequestStatusEnum.InReview
      })
        .then(() => {
          this.$emit('start-review');
          this.loadNextPayrollChangeRequestHRPersonnels();
          this.fetchPayrollChangeRequestActivities(
            this.payrollChangeRequest.id
          );
        })
        .catch(error => {
          if (error.response && error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * @see api payrollChangeRequest/models/complete
     * @param {number} assignee
     * can be called only from states:
     * - InReview
     *
     * can go to states
     * - Complete
     */
    async complete(assignee: number): Promise<void> {
      this.clearAllErrors();
      this.loading = true;

      this.updatePayrollChangeRequestState({
        payrollChangeRequestId: this.payrollChangeRequest.id,
        state: PayrollChangeRequestStatusEnum.Complete,
        assignee: assignee
      })
        .then(() => {
          this.fetchPayrollChangeRequestActivities(
            this.payrollChangeRequest.id
          );
        })
        .catch(error => {
          if (error.response && error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * @see api payrollChangeRequest/models/cancel
     * can be called only from states:
     * - Any state
     *
     * can go to states
     * - Cancel
     */
    async cancel(): Promise<void> {
      this.clearAllErrors();
      this.loading = true;
      this.updatePayrollChangeRequestState({
        payrollChangeRequestId: this.payrollChangeRequest.id,
        state: PayrollChangeRequestStatusEnum.Cancelled
      })
        .then(() => {
          this.$emit('cancel');
          this.fetchPayrollChangeRequestActivities(
            this.payrollChangeRequest.id
          );
        })
        .catch(error => {
          if (error.response && error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    clearAllErrors(): void {
      this.errorMessage = '';
      this.errorMessageDetail = '';
    },

    loadNextPayrollChangeRequestHRPersonnels() {
      this.hrPersonnelLoading = true;
      return this.fetchPayrollChangeRequestHRPersonnels(
        this.payrollChangeRequest.id
      )
        .catch(() => {
          this.errorMessage = 'Error getting hrPersonnels';
        })
        .finally(() => (this.hrPersonnelLoading = false));
    }
  }
});
